import { useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { shadows, colours } from '@qflow/theme'
import { NavLink } from 'react-router-dom'
import { QflowLogo } from './Icons/QflowLogo'

import { Queries } from 'admin/lib/Api'
import { env } from 'admin/lib/config/env-config'
import * as ROUTES from 'admin/routes'

export const Layout = ({
  header = null,
  children
}: {
  header: JSX.Element | null
  children: JSX.Element | JSX.Element[]
}) => {
  const user = Queries.useUserSelf()
  const toggleDarkMode = () => {
    const darkMode = localStorage.getItem('darkMode')
    if (darkMode) {
      localStorage.removeItem('darkMode')
      document.body.classList.remove('dark-mode')
    } else {
      localStorage.setItem('darkMode', 'true')
      document.body.classList.add('dark-mode')
    }
  }

  // Default menu dropdowns to be closed
  const [dropdownOpen, setDropdownOpen] = useState([false, false])

  const handleDropdownOpen = dropdownIndex => {
    const tempDropdownOpen = [false, false]
    const currentDropdownOpen = dropdownOpen[dropdownIndex]
    if (!currentDropdownOpen) {
      tempDropdownOpen[dropdownIndex] = true
    }
    setDropdownOpen(tempDropdownOpen)
  }

  const Dropdown = ({ title, menu, dropdownOpen, handleDropdownOpen }) => {
    return (
      <div>
        <NavbarItem onClick={handleDropdownOpen}>{title}</NavbarItem>
        {dropdownOpen ? (
          <div className="Menu">
            {menu.map((item, index) => (
              <MenuItem key={index}>{item}</MenuItem>
            ))}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <OuterContainer>
      <HeaderContainer>
        <HeaderTier>
          <NavbarLink
            id="logo-link"
            to={ROUTES.RECORDS_SUMMARY.LIST}
            title={`Qflow Admin | ${env.CI_BUILDNUMBER || 'dev'}`}
          >
            <LogoIcon />
          </NavbarLink>
          <NavbarLink to={ROUTES.INTRAY}>In Tray</NavbarLink>
          <NavbarLink to={ROUTES.ACCOUNTS.LIST}>Accounts</NavbarLink>
          <Dropdown
            title="Data"
            menu={[
              <NavbarLink target="_blank" to={ROUTES.REFERENCEITEMS.LIST}>
                Reference Items
              </NavbarLink>,
              <NavbarLink target="_blank" to={ROUTES.CERTIFICATES.LIST}>
                Certificates
              </NavbarLink>,
              <NavbarLink target="_blank" to={ROUTES.COMPANIES.LIST}>
                Companies
              </NavbarLink>,
              <NavbarLink target="_blank" to={ROUTES.PUBLIC_API_KEYS.LIST}>
                Public Keys
              </NavbarLink>,
              <NavbarLink target="_blank" to={ROUTES.USERS.LIST}>
                Users
              </NavbarLink>,
              <NavbarLink target="_blank" to={ROUTES.BI_REPORTS.INDEX}>
                BI Reports
              </NavbarLink>
            ]}
            dropdownOpen={dropdownOpen[0]}
            handleDropdownOpen={() => handleDropdownOpen(0)}
          />
          {user.data && user.data.isSuper && (
            <Dropdown
              title="Advanced"
              menu={[
                <NavbarLink target="_blank" to={ROUTES.GROUP_UPDATE.EDIT}>
                  Qupid
                </NavbarLink>,
                <NavbarLink target="_blank" to={ROUTES.GROUP_UPDATE.LOG}>
                  Qupid Log
                </NavbarLink>
              ]}
              dropdownOpen={dropdownOpen[1]}
              handleDropdownOpen={() => handleDropdownOpen(1)}
            />
          )}

          <div style={{ flexGrow: 1 }}></div>

          <div>
            <button
              id="dark-mode-btn"
              className="ant-btn ant-btn-primary"
              title="Toggle dark mode"
              style={{ padding: '7px' }}
              onClick={toggleDarkMode}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="17"
                viewBox="0 0 16 17"
                fill="none"
              >
                <path
                  d="M7.99992 15.1666C11.6799 15.1666 14.6666 12.18 14.6666 8.49998C14.6666 4.81998 11.6799 1.83331 7.99992 1.83331C4.31992 1.83331 1.33325 4.81998 1.33325 8.49998C1.33325 12.18 4.31992 15.1666 7.99992 15.1666ZM8.66659 3.21331C11.2933 3.53998 13.3333 5.77998 13.3333 8.49998C13.3333 11.22 11.2999 13.46 8.66659 13.7866V3.21331Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </HeaderTier>

        {header && (
          <HeaderTier2ndRow className="header-tier-2">
            {header}
          </HeaderTier2ndRow>
        )}
      </HeaderContainer>

      <BodyContainer className={'Layouts__BodyContainer'}>{children}</BodyContainer>
    </OuterContainer>
  )
}

Layout.propTypes = {
  header: PropTypes.any,
  children: PropTypes.any
}

export const OuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

export const MenuItem = styled.div`
  margin: 0;
  padding: 0rem 0.5rem;
  background-color: inherit;
  font-size: 1rem;
  font-weight: 500;

  &:hover {
    border-left: solid;
    border-width: 0.25rem;
    border-bottom-color: ${colours.LIGHT_TEAL};
    color: ${colours.LIGHT_TEAL};
    cursor: pointer;
  }
`

export const NavbarLink = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  height: 1.25rem;
  padding: 0.5rem 0;
  font-size: 1rem;
  font-weight: 500;
  color: ${colours.BATTLESHIP_GREY};

  &:hover {
    color: ${colours.TEAL};
    cursor: pointer;
  }

  &.active {
    color: ${colours.TEAL};
  }
`

export const NavbarItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  height: 1.25rem;
  padding: 0.5rem 0;
  font-size: 1rem;
  font-weight: 500;
  color: ${colours.BATTLESHIP_GREY};

  &:hover {
    color: ${colours.TEAL};
    cursor: pointer;
  }
`
export const HeaderTier = styled.div`
  flex-direction: row;
  display: flex;
  flex-grow: 1;
  align-items: center;
  gap: 1rem;
  padding-bottom: 10px;
`

export const HeaderTier2ndRow = styled.div`
  flex-direction: row;
  display: flex;
  flex-grow: 1;
  align-items: center;
  gap: 0.5rem;
  margin: -10px -10px 0;
  padding: 8px 10px;
`

export const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: left;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 10px 10px 0;
  box-shadow: 0px 0px 11px 0px rgba(0, 0, 0, 0.1);
  z-index: 100;
`

export const BodyContainer = styled.section`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;

  height: 100%;

  overflow: auto;
`

export const BodyContentColumn = styled.div`
  width: 100%;
  max-width: 40rem;
  margin: 2rem;
`

export const ContentContainer = styled.div`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  height: 100%;
  overflow: hidden;
  padding: 0;
`

export const Card = styled.div`
  display: flex;
  flex: 0 0 auto;

  max-width: 30rem;
  width: 100%;

  margin: 1rem;

  border-radius: 0.5rem;
  overflow: hidden;
  ${shadows.MID};
`

export const ContentFooter = styled.div`
  display: flex;
  flex: 0 0 3rem;
  flex-direction: row;
  position: relative;
  width: 100%;

  justify-content: center;
  align-items: center;

  padding: 0 1rem;
  border-top: 1px rgba(0, 0, 0, 0.2) solid;
`

export const ContentFooterCentredOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  pointer-events: none;
  > * {
    pointer-events: initial;
  }

  button > .anticon > svg {
    width: 1rem;
    height: 1rem;
    margin-top: 5px;
  }
`

export const Spacer = styled.div`
  display: flex;
  flex: 0 0 0.25rem;
`

export const Fill = styled.div`
  flex: 1 1 0;
`
export const ContentCentred = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  button > .anticon > svg {
    width: 1rem;
    height: 1rem;
    margin-top: 7px;
  }
`

export const LogoIcon = styled(QflowLogo).attrs(() => ({
  style: {}
}))`
  width: 63px;
  height: 20px;
  display: block;
  margin: 0 0 2px 0;
`
